import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Search, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { Dialog } from "./Dialog";

type Content = {
  settings: Record<string, any>;
  faqs: Array<{ publicId: string; question: string; answer: string }>;
};

type KnowledgeEntry = { key: string; question: string; answer: string };

function buildKnowledgeBase(content?: Content): KnowledgeEntry[] {
  if (!content) return [];
  const entries: KnowledgeEntry[] = content.faqs.map((faq) => ({ key: faq.publicId, question: faq.question, answer: faq.answer }));
  const policies = content.settings.policies ?? {};
  const contact = content.settings.contact ?? {};
  if (policies.payment) entries.push({ key: "policy-payment", question: "What is your payment policy?", answer: policies.payment });
  if (policies.variation) entries.push({ key: "policy-variation", question: "Will my item look exactly like the photo?", answer: policies.variation });
  if (contact.email || contact.mobile) {
    entries.push({
      key: "contact-info",
      question: "How can I reach you?",
      answer: [contact.email && `Email: ${contact.email}`, contact.mobile && `Mobile: ${contact.mobile}`].filter(Boolean).join(" · ")
    });
  }
  if (contact.pickupAddress) entries.push({ key: "contact-pickup", question: "Where can I pick up my order?", answer: contact.pickupAddress });
  return entries;
}

function searchKnowledgeBase(entries: KnowledgeEntry[], query: string) {
  const words = query.toLowerCase().split(/\s+/).filter((word) => word.length > 2);
  if (!words.length) return [];
  return entries
    .map((entry) => {
      const haystack = `${entry.question} ${entry.answer}`.toLowerCase();
      const score = words.reduce((total, word) => total + (haystack.includes(word) ? 1 : 0), 0);
      return { entry, score };
    })
    .filter((result) => result.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((result) => result.entry);
}

export function HelpWidget({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [message, setMessage] = useState("");

  const content = useQuery({ queryKey: ["content"], queryFn: () => api<Content>("/content") });
  const knowledgeBase = useMemo(() => buildKnowledgeBase(content.data), [content.data]);
  const results = useMemo(() => searchKnowledgeBase(knowledgeBase, query), [knowledgeBase, query]);

  const send = useMutation({
    mutationFn: () => api<{ publicId: string }>("/help/messages", {
      method: "POST",
      body: JSON.stringify({ name, contact, message, context: query.trim() || undefined })
    }),
    onSuccess: () => { setName(""); setContact(""); setMessage(""); }
  });

  const reset = () => { setQuery(""); setShowForm(false); send.reset(); };
  const shown = query.trim().length === 0 ? knowledgeBase.slice(0, 4) : results;

  return <Dialog
    open={open}
    onClose={onClose}
    title={send.isSuccess ? "Message sent" : showForm ? "Write to Cass" : "Ask Cass"}
    description={send.isSuccess ? undefined : showForm ? "Cass replies personally, usually within a day." : "Search common questions, or send a message."}
  >
    {!showForm && !send.isSuccess && <>
      <label className="relative block">
        <span className="sr-only">Search questions</span>
        <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
        <input
          autoFocus
          className="field pl-10"
          placeholder="e.g. how long will my order take?"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      <div className="mt-4 divide-y divide-line border-y border-line">
        {shown.map((entry) => <details key={entry.key} className="group py-1">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-3 text-[15px] font-bold text-cocoa [&::-webkit-details-marker]:hidden">
            {entry.question}<ChevronDown size={17} className="shrink-0 text-muted transition-transform group-open:rotate-180" />
          </summary>
          <p className="pb-3 text-sm leading-relaxed text-muted">{entry.answer}</p>
        </details>)}
        {query.trim().length > 0 && results.length === 0 && <p className="py-4 text-sm text-muted">No answer matches that yet. Send Cass a message instead.</p>}
      </div>
      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="font-hand text-xl leading-tight text-ribbon-ink">still curious?</p>
        <button onClick={() => setShowForm(true)} className="btn-secondary min-h-11 px-5 text-sm">Send a message</button>
      </div>
    </>}
    {showForm && !send.isSuccess && <form onSubmit={(event) => { event.preventDefault(); send.mutate(); }} className="grid gap-4">
      <label><span className="label">Name</span><input required className="field" autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} /></label>
      <label><span className="label">Email or mobile</span><input required className="field" value={contact} onChange={(event) => setContact(event.target.value)} /></label>
      <label><span className="label">Message</span><textarea required className="field min-h-28" value={message} onChange={(event) => setMessage(event.target.value)} /></label>
      {send.isError && <p role="alert" className="text-sm font-bold text-ribbon-ink">{send.error.message}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={() => setShowForm(false)} className="btn-secondary flex-1">Back</button>
        <button type="submit" disabled={send.isPending} className="btn-primary flex-1"><Send size={16} /> {send.isPending ? "Sending…" : "Send message"}</button>
      </div>
    </form>}
    {send.isSuccess && <div className="grid justify-items-start gap-3 py-2">
      <span className="grid size-11 place-items-center rounded-full bg-mint-soft text-mint"><Check size={20} /></span>
      <p className="text-[15px] leading-relaxed text-muted">Cass will reply to the email or mobile number you gave.</p>
      <button onClick={reset} className="link-quiet mt-1 text-sm">Ask something else</button>
    </div>}
  </Dialog>;
}
